import { BottomDrawerFactory } from '@rendora/common/features/bottom-drawer/factory';
import { ModalFactory } from '@rendora/common/features/modals/factory';
import { useBreakpoint } from '@rendora/design/index';
import { h } from 'vue';
import ProtocolContent from '../ui/ProtocolContent.vue';

const symbolProtocolConfirm = Symbol('protocol-confirm');

/**
 * 协议确认弹窗 composable
 * 提供统一的协议确认弹窗管理功能
 */
export function useProtocolConfirm() {
  const headers = useRequestHeaders();
  const { isMobile } = useBreakpoint(headers?.['user-agent']);

  /**
   * 关闭协议确认弹窗
   */
  const close = () => {
    if (isMobile.value) {
      const drawer = BottomDrawerFactory.getInstance(symbolProtocolConfirm);
      drawer?.close();
    } else {
      const modal = ModalFactory.getInstance(symbolProtocolConfirm);
      modal?.close();
    }
  };

  /**
   * 显示协议确认弹窗
   * @param onConfirm 确认回调函数
   * @returns ModalInstance
   */
  const showModal = (onConfirm: () => void) => {
    const handleConfirm = () => {
      onConfirm();
      const modal = ModalFactory.getInstance(symbolProtocolConfirm);
      modal?.close();
    };

    // 检查是否已存在弹窗实例
    if (ModalFactory.hasModal(symbolProtocolConfirm)) {
      const modal = ModalFactory.getInstance(symbolProtocolConfirm);
      modal?.update({
        content: h(ProtocolContent, { onConfirm: handleConfirm, onCancel: close }),
      });
      return modal;
    }

    // 创建新的弹窗实例
    const modal = ModalFactory.create(
      {
        title: ' ',
        width: '500px',
        height: '198px',
        showHeader: true,
        showCloseButton: true,
        closeOnOverlayClick: false,
        closeOnEsc: true,
        maskClosable: false,
        centered: true,
        content: h(ProtocolContent, { onConfirm: handleConfirm, onCancel: close }),
      },
      symbolProtocolConfirm,
    );

    return modal;
  };

  /**
   * 显示协议确认底部抽屉（移动端）
   * @param onConfirm 确认回调函数
   * @returns BottomDrawerInstance
   */
  const showBottomDrawer = (onConfirm: () => void) => {
    const handleConfirm = () => {
      onConfirm();
      const drawer = BottomDrawerFactory.getInstance(symbolProtocolConfirm);
      drawer?.close();
    };

    // 检查是否已存在抽屉实例
    if (BottomDrawerFactory.hasDrawer(symbolProtocolConfirm)) {
      const drawer = BottomDrawerFactory.getInstance(symbolProtocolConfirm);
      drawer?.update({
        content: h(ProtocolContent, { onConfirm: handleConfirm, onCancel: close }),
      });
      return drawer;
    }

    // 创建新的抽屉实例
    const drawer = BottomDrawerFactory.create(
      {
        content: h(ProtocolContent, { onConfirm: handleConfirm, onCancel: close }),
        draggable: true,
        closeOnClickMask: true,
        lockScroll: true,
      },
      symbolProtocolConfirm,
    );

    return drawer;
  };

  const show = (confirm: () => void) => {
    return isMobile.value ? showBottomDrawer(confirm) : showModal(confirm);
  };

  return {
    show,
    close,
  };
}
